import { useEffect, useMemo, useState } from 'react';
import { AccordionListItem, LinkItem } from '../../common/types/accordion-list-item';
import { faqs, homepageFaqs, marketSpecificFaqs } from '../constants/faqs';
import { Market } from '@/common/types/market';
import Spacer from '@/common/components/spacer';
import { Link } from 'react-router';
import { Button } from '@/shadcn-ui/button';
import AccordionList from '@/common/components/accordion-list';
import { cn } from '@/shadcn-ui/utils';
import { withPageContainerStyles } from '@/common/components/page-layout';
import useGetActiveMarketsCopy from '../hooks/use-get-active-markets-copy';
import { useDisableSpaNavigation } from '@/util-react-router/hooks/use-disable-spa-navigation';

export type FaqsProps = {
  currentMarket?: Market;
  markets: Market[];
  supportsDelivery?: boolean;
  variant?: string;
  isSubset?: boolean;
};

export default function Faqs({
  variant = 'light',
  markets,
  currentMarket,
  supportsDelivery = true,
  isSubset = false,
}: FaqsProps) {
  const { disableSpaNavigation } = useDisableSpaNavigation();

  const whichTypeOfFaq = useMemo(() => {
    return isSubset ? homepageFaqs(disableSpaNavigation) : faqs(disableSpaNavigation);
  }, [isSubset, disableSpaNavigation]);

  const [items, setItems] = useState<AccordionListItem[]>([]);

  const isDark = variant === 'dark';
  // Current state: we have to resort to comparing market strings cause UI knows what market is selected
  // Future state: CMS implementation, FAQs should be part of the response
  const getMarketSpecificFAQs = (marketName: string | null) => {
    return marketName && marketSpecificFaqs[marketName]
      ? marketSpecificFaqs[marketName]
      : [];
  };
  // Hide link for specific markets
  const filterLink = (marketName: string | null, link: LinkItem) =>
    marketName && link?.availableFor?.includes(marketName) ? link : {};

  const activeMarkets = useGetActiveMarketsCopy(markets);

  useEffect(() => {
    setItems([
      ...getMarketSpecificFAQs(currentMarket?.name ?? ''),
      ...whichTypeOfFaq.map((item) => {
        return {
          title: item.title,
          content: item.content.map((contentItem) => ({
            ...contentItem,
            body:
              typeof contentItem.body === 'string'
                ? contentItem.body.replace('{ACTIVE_MARKETS}', activeMarkets)
                : contentItem.body,
          })),
          list: item.list,
          link: item.link ? filterLink(currentMarket?.name ?? '', item.link) : {},
        };
      }),
    ]);
  }, [activeMarkets, currentMarket?.name, supportsDelivery, whichTypeOfFaq]);

  return (
    <div
      className={cn(`w-full`, {
        'bg-flexcar-blue10': isDark,
        'bg-flexcar-neutral90': !isDark,
      })}
    >
      <div
        className={withPageContainerStyles(
          `mx-auto my-0 flex min-h-[710px] max-w-[1440px] flex-col items-center py-[80px]`,
          {
            'mt-auto': !isDark,
          }
        )}
      >
        <h1
          className={cn(`text-left md:text-center`, {
            'w-full text-flexcar-neutral100': isDark,
            'text-flexcar-neutral10': !isDark,
          })}
        >
          Frequently asked questions
        </h1>
        <Spacer variant="height24" />
        <AccordionList variant={isDark ? 'dark' : 'default'} accordionItems={items} />
        <Spacer variant="height8" />
        <Link
          to="/how-it-works#faq"
          reloadDocument={true}
          className="w-full md:w-[410px]"
        >
          <Button variant="secondary" className="w-full md:w-[410px]">
            See all FAQs
          </Button>
        </Link>
      </div>
    </div>
  );
}
